// ── Generic 3D game table: a real plane leaning away from the camera,
//    carrying an etched inlay and the cards. Each game skins it. ──

import { motion } from 'framer-motion';
import { SCENE } from '../../lib/battle3d';

/**
 * surface — { top, mid, low, deep } body colours
 * trim    — { rim, rivet, ink, accent, glow } metal, inlay and light colours
 * inlay   — React node etched into the table top (map, blueprint, wheel…)
 */
const Table3D = ({
  children, mobile = false, active = false,
  surface = { top: '#2a1c0e', mid: '#1a1006', low: '#241708', deep: '#140b04' },
  trim = { rim: 'rgba(120,100,66,0.85)', rivet: '#b9a377', ink: '#3a2a08', accent: '#ffd700', glow: 'rgba(251,191,36,0.26)' },
  inlay = null,
  maxWidth,
  grain = 94,
}) => {
  const tilt = mobile ? SCENE.tableTiltMobile : SCENE.tableTilt;
  const pad = mobile ? '20px 14px 24px' : '46px 54px 54px';

  return (
    <div style={{
      perspective: mobile ? SCENE.perspectiveMobile : SCENE.perspective,
      perspectiveOrigin: '50% 30%',
      width: '100%', display: 'flex', justifyContent: 'center',
    }}>
      <motion.div
        initial={{ rotateX: tilt + 16, opacity: 0, y: 40 }}
        animate={{ rotateX: tilt, opacity: 1, y: 0 }}
        transition={{ duration: 1.1, ease: [0.2, 0.7, 0.2, 1] }}
        style={{
          transformStyle: 'preserve-3d',
          position: 'relative',
          borderRadius: mobile ? 16 : 26,
          padding: pad,
          maxWidth: maxWidth ?? (mobile ? 264 : 760),
          width: '100%',
          background: `
            radial-gradient(ellipse 70% 55% at 50% 42%, rgba(255,255,255,0.07) 0%, transparent 62%),
            repeating-linear-gradient(${grain}deg, rgba(0,0,0,0.30) 0 2px, transparent 2px 13px),
            linear-gradient(160deg, ${surface.top} 0%, ${surface.mid} 38%, ${surface.low} 70%, ${surface.deep} 100%)`,
          boxShadow: `
            0 0 0 2px ${trim.rim},
            0 0 0 ${mobile ? 5 : 8}px rgba(0,0,0,0.75),
            0 ${mobile ? 16 : 34}px ${mobile ? 28 : 56}px rgba(0,0,0,0.85),
            inset 0 2px 0 rgba(255,255,255,0.12),
            inset 0 -18px 34px rgba(0,0,0,0.75)`,
        }}
      >
        {/* Etched inlay */}
        {inlay && (
          <div style={{
            position: 'absolute', inset: mobile ? 8 : 16, borderRadius: mobile ? 10 : 18, overflow: 'hidden',
            background: `linear-gradient(150deg, ${trim.ink}cc, rgba(6,4,2,0.92))`,
            boxShadow: 'inset 0 0 40px rgba(0,0,0,0.9), inset 0 2px 0 rgba(255,255,255,0.06)',
          }}>
            {inlay}
            <div style={{ position: 'absolute', inset: 0, background: `radial-gradient(ellipse 60% 50% at 50% 40%, ${trim.glow} 0%, transparent 70%)` }} />
          </div>
        )}

        {/* Overhead light pool */}
        <motion.div
          animate={{ opacity: [0.5, 0.85, 0.6, 0.8, 0.5] }}
          transition={{ duration: 3.4, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            position: 'absolute', inset: 0, borderRadius: 'inherit', pointerEvents: 'none',
            background: `radial-gradient(ellipse 55% 45% at 50% 34%, ${trim.glow} 0%, transparent 68%)`,
          }}
        />

        {/* Rivets */}
        {!mobile && Array.from({ length: 18 }, (_, i) => {
          const onTop = i < 9;
          const p = (onTop ? i / 18 : i / 18 - 0.5) * 2;
          return (
            <div key={i} style={{
              position: 'absolute',
              top: onTop ? 7 : undefined, bottom: onTop ? undefined : 7,
              left: `${6 + p * 88}%`, width: 7, height: 7, borderRadius: '50%',
              background: `radial-gradient(circle at 35% 30%, ${trim.rivet} 0%, rgba(0,0,0,0.6) 70%, #000 100%)`,
              boxShadow: 'inset 0 -1px 1px rgba(0,0,0,0.8), 0 1px 2px rgba(0,0,0,0.9)',
            }} />
          );
        })}

        {/* Corner brackets */}
        {[['top', 'left'], ['top', 'right'], ['bottom', 'left'], ['bottom', 'right']].map(([v, h], i) => (
          <div key={i} style={{
            position: 'absolute', [v]: 0, [h]: 0,
            width: mobile ? 22 : 44, height: mobile ? 22 : 44,
            [`border${v === 'top' ? 'Top' : 'Bottom'}`]: `3px solid ${trim.rim}`,
            [`border${h === 'left' ? 'Left' : 'Right'}`]: `3px solid ${trim.rim}`,
            borderRadius: mobile
              ? (v === 'top' ? (h === 'left' ? '16px 0 0 0' : '0 16px 0 0') : (h === 'left' ? '0 0 0 16px' : '0 0 16px 0'))
              : (v === 'top' ? (h === 'left' ? '26px 0 0 0' : '0 26px 0 0') : (h === 'left' ? '0 0 0 26px' : '0 0 26px 0')),
            pointerEvents: 'none',
          }} />
        ))}

        {/* Turn pulse running the rim */}
        {active && (
          <motion.div
            animate={{ opacity: [0.2, 0.9, 0.2] }}
            transition={{ duration: 1.5, repeat: Infinity, ease: 'easeInOut' }}
            style={{
              position: 'absolute', inset: -3, borderRadius: 'inherit', pointerEvents: 'none',
              border: `2px solid ${trim.accent}`, boxShadow: `0 0 26px ${trim.glow}, inset 0 0 26px ${trim.glow}`,
            }}
          />
        )}

        {/* Cards, standing proud of the surface */}
        <div style={{ position: 'relative', transformStyle: 'preserve-3d', transform: 'translateZ(8px)' }}>
          {children}
        </div>
      </motion.div>
    </div>
  );
};

export default Table3D;
